import { action, computed, makeObservable, observable } from 'mobx';
import * as cadApi from '@mark43/cad-api';
import { ComparatorReturns, ListStore, SortingDirective } from '@list-store';
import { RelatedDataType } from './types';
import { RelatedDataView } from '@mark43/cad-api';

export type RelatedDataEntityWithId = cadApi.RelatedDataEntity & { id: number };

const mapFromEntityId = (entity: cadApi.RelatedDataEntity): RelatedDataEntityWithId => {
    const { entityId } = entity;
    return {
        ...entity,
        id: entityId,
    };
};

const sortingDirective: SortingDirective<RelatedDataEntityWithId> = {
    getSortValue: (entity) => entity.id,
    comparator: (left: number, right: number) => Math.sign(right - left) as ComparatorReturns,
};

const entityKeyMap = {
    CAD_EVENTS: {
        entityStoreKey: 'cadEventsStore',
        entityCountKey: 'cadEventsCount',
        entityNextFromKey: 'cadEventsNextFrom',
    },
    RMS_REPORTS: {
        entityStoreKey: 'rmsReportsStore',
        entityCountKey: 'rmsReportsCount',
        entityNextFromKey: 'rmsReportsNextFrom',
    },
    VEHICLES: {
        entityStoreKey: 'vehiclesStore',
        entityCountKey: 'vehiclesCount',
        entityNextFromKey: 'vehiclesNextFrom',
    },
} as const;

export class RelatedDataStore {
    @observable
    cadEventsStore: ListStore<RelatedDataEntityWithId> = new ListStore({ sortingDirective });
    @observable
    cadEventsNextFrom: number | null = null;
    @observable
    cadEventsCount: number = 0;

    @observable
    rmsReportsStore: ListStore<RelatedDataEntityWithId> = new ListStore();
    @observable
    rmsReportsNextFrom: number | null = null;
    @observable
    rmsReportsCount: number = 0;

    @observable
    vehiclesStore: ListStore<RelatedDataEntityWithId> = new ListStore();
    @observable
    vehiclesNextFrom: number | null = null;
    @observable
    vehiclesCount: number = 0;

    @observable
    isLoadingPoll: boolean = false;
    @observable
    isLoadingViewMore: boolean = false;

    @observable
    isCadEventsPollEnabled: boolean = true;
    @observable
    isRmsReportsPollEnabled: boolean = true;
    @observable
    isVehiclesPollEnabled: boolean = true;

    eventId?: number;
    enhancedRelatedDataEnabled: boolean;

    constructor(enhancedRelatedDataEnabled: boolean, eventId?: number) {
        this.eventId = eventId;
        this.enhancedRelatedDataEnabled = enhancedRelatedDataEnabled;

        makeObservable(this);
    }

    /**
     * Updates stores based on the provided related data view from the API. This function handles the logic
     * for either upserting or replacing items in the respective stores for each related data entity type
     * based on the received data. It also manages updating the 'next from' pagination pointers as necessary.
     */
    @action
    update = ({
        relatedDataView,
        typesToReplace,
        typesToUpsert,
        typesToUpdateNextFrom,
    }: {
        relatedDataView: RelatedDataView;
        typesToReplace: RelatedDataType[];
        typesToUpsert: RelatedDataType[];
        typesToUpdateNextFrom: RelatedDataType[];
    }) => {
        /**
         * Updates the related data entity store for the specified entity type
         * This includes upserting or replacing entities, updating the entity count and updating the entities pagination (next from)
         *
         * @param {RelatedDataType} entityType - The type of entity to update.
         * @param {cadApi.RelatedDataEntity[]} fetchedEntities - The entities fetched from the API that need updating.
         */
        const updateRelatedDataEntity = (
            entityType: RelatedDataType,
            fetchedEntities: cadApi.RelatedDataEntity[]
        ) => {
            const newEntityRows = fetchedEntities.map(mapFromEntityId);

            const { entityStoreKey, entityCountKey, entityNextFromKey } = entityKeyMap[entityType];

            if (typesToUpsert.includes(entityType)) {
                this[entityStoreKey].upsertItems(newEntityRows);
            } else if (typesToReplace.includes(entityType)) {
                this[entityStoreKey].setItems(newEntityRows);
                this[entityCountKey] = relatedDataView[entityCountKey];
            }
            if (typesToUpdateNextFrom.includes(entityType)) {
                this[entityNextFromKey] = relatedDataView[entityNextFromKey] ?? null;
            }
        };

        const { cadEvents, rmsReports, vehicles } = relatedDataView;

        updateRelatedDataEntity('CAD_EVENTS', cadEvents);
        updateRelatedDataEntity('RMS_REPORTS', rmsReports);
        updateRelatedDataEntity('VEHICLES', vehicles);
    };

    @action
    setIsLoadingViewMore = (isLoadingViewMore: boolean): void => {
        this.isLoadingViewMore = isLoadingViewMore;
    };

    @action
    setIsLoadingPoll = (isLoadingPoll: boolean): void => {
        this.isLoadingPoll = isLoadingPoll;
    };

    @action
    setIsCadEventsPollEnabled = (isCadEventsPollEnabled: boolean): void => {
        this.isCadEventsPollEnabled = isCadEventsPollEnabled;
    };

    @action
    setIsRmsReportsPollEnabled = (isRmsReportsPollEnabled: boolean): void => {
        this.isRmsReportsPollEnabled = isRmsReportsPollEnabled;
    };

    @action
    setIsVehiclesPollEnabled = (isVehiclesPollEnabled: boolean): void => {
        this.isVehiclesPollEnabled = isVehiclesPollEnabled;
    };

    @computed
    get cadEvents(): cadApi.RelatedDataEntity[] {
        return this.cadEventsStore.data.list;
    }

    @computed
    get rmsReports(): cadApi.RelatedDataEntity[] {
        return this.rmsReportsStore.data.list;
    }

    @computed
    get vehicles(): cadApi.RelatedDataEntity[] {
        return this.vehiclesStore.data.list;
    }

    @computed
    get hasMoreCadEvents(): boolean {
        return this.cadEventsNextFrom !== null;
    }

    @computed
    get hasMoreRmsReports(): boolean {
        return this.rmsReportsNextFrom !== null;
    }

    @computed
    get hasMoreVehicles(): boolean {
        return this.vehiclesNextFrom !== null;
    }

    @computed
    get isNewEvent(): boolean {
        return this.eventId === undefined;
    }

    @computed
    get isPollEnabled(): boolean {
        return (
            this.isCadEventsPollEnabled ||
            this.isRmsReportsPollEnabled ||
            this.isVehiclesPollEnabled
        );
    }
}
